import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FaArrowRight, FaMapMarkerAlt } from 'react-icons/fa';
import { getZoneBySlug, zones } from '../data/zones';
import { services } from '../data/services';

const ZoneIntervention = () => {
  const { slug } = useParams();
  const zone = getZoneBySlug(slug);

  if (!zone) {
    return <Navigate to="/" replace />;
  }

  // Schema.org LocalBusiness for this zone
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Olney Tech Solutions - ${zone.name}`,
    "description": zone.description,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": zone.name,
      "addressRegion": "Hauts-de-France",
      "addressCountry": "FR"
    },
    "telephone": "+33689177385",
    "email": "contact@olney.dev",
    "url": `https://olney.dev/zone-intervention/${zone.slug}`,
    "areaServed": {
      "@type": "City",
      "name": zone.name
    },
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 50.75,
        "longitude": 3.12
      },
      "geoRadius": "30000"
    }
  };

  const otherZones = zones.filter(z => z.slug !== slug).slice(0, 6);

  return (
    <>
      <Helmet>
        <title>Agence Web à {zone.name} | Création Site Internet | Olney Tech Solutions</title>
        <meta name="description" content={`Création de sites web et dépannage informatique à ${zone.name}. Agence web locale, intervention rapide. Sites vitrines, e-commerce, maintenance.`} />
        <link rel="canonical" href={`https://olney.dev/zone-intervention/${zone.slug}`} />
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      </Helmet>

      {/* Breadcrumb */}
      <div className="pt-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-gray-400">
            <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
            <span>/</span>
            <span className="text-white">Zone d'intervention : {zone.name}</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[var(--primary)] rounded-full blur-[150px] opacity-10 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full border border-[var(--primary)] bg-[rgba(112,0,255,0.1)] text-[var(--primary)]">
                <FaMapMarkerAlt />
                <span className="text-sm font-bold uppercase tracking-wider">Zone d'intervention</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 font-['Space_Grotesk'] text-white">
                Agence Web à <span className="text-gradient-electric">{zone.name}</span>
              </h1>

              <p className="text-xl text-gray-400 mb-8 leading-relaxed">
                {zone.description}
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <div className="glass-panel rounded-xl px-4 py-3">
                  <span className="text-gray-400 text-sm">Population</span>
                  <div className="text-white font-bold">{zone.population} hab.</div>
                </div>
                <div className="glass-panel rounded-xl px-4 py-3">
                  <span className="text-gray-400 text-sm">Distance siège</span>
                  <div className="text-white font-bold">{zone.distance}</div>
                </div>
                <div className="glass-panel rounded-xl px-4 py-3">
                  <span className="text-gray-400 text-sm">Région</span>
                  <div className="text-white font-bold">{zone.region}</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="btn-magnetic px-8 py-4 bg-[var(--primary)] text-white font-bold rounded-xl hover:opacity-90 transition-all inline-flex items-center gap-2"
                >
                  Demander un devis <FaArrowRight />
                </Link>
                <a
                  href="tel:+33689177385"
                  className="px-8 py-4 border border-white/20 text-white font-bold rounded-xl hover:bg-white/5 transition-all"
                >
                  06 89 17 73 85
                </a>
              </div>
            </motion.div>

            {/* Map Placeholder */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-panel rounded-3xl p-8 aspect-square flex items-center justify-center"
            >
              <div className="text-center">
                <FaMapMarkerAlt className="text-[var(--primary)] text-6xl mb-4 mx-auto" />
                <h3 className="text-white font-bold text-xl mb-2">{zone.name}</h3>
                <p className="text-gray-400">Intervention rapide sur site</p>
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  {zone.nearbyAreas.map((area, idx) => (
                    <span key={idx} className="px-3 py-1 rounded-full bg-white/10 text-gray-300 text-sm">
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services disponibles */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-white font-['Space_Grotesk']">
              Nos services à {zone.name}
            </h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              Tous nos services sont disponibles à {zone.name} et ses environs.
              Intervention sur site ou à distance selon vos besoins.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, idx) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link
                  to={`/services/${service.slug}`}
                  className={`glass-panel rounded-2xl p-6 block hover:border-[var(--${service.color})]/50 transition-colors group`}
                >
                  <h3 className="text-xl font-bold text-white mb-3 font-['Space_Grotesk'] group-hover:text-[var(--primary)] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {service.description}
                  </p>
                  <div className="flex justify-between items-center">
                    <span className={`text-[var(--${service.color})] text-sm font-bold`}>
                      À partir de {service.pricing.starting}{service.pricing.unit}
                    </span>
                    <FaArrowRight className="text-gray-500 group-hover:text-[var(--primary)] group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-white font-['Space_Grotesk']">
              Pourquoi nous choisir à {zone.name} ?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '🚀', title: 'Intervention rapide', desc: 'Sur site sous 24-48h' },
              { icon: '💬', title: 'Proximité', desc: 'Un interlocuteur local et disponible' },
              { icon: '💡', title: 'Expertise', desc: '5+ ans d\'expérience' },
              { icon: '🔒', title: 'Garantie', desc: 'Satisfaction garantie' }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-panel rounded-2xl p-6 text-center"
              >
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-white font-bold mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mots-clés SEO */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl font-bold text-white font-['Space_Grotesk']">
              Services recherchés à {zone.name}
            </h2>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-3">
            {zone.keywords.map((keyword, idx) => (
              <span
                key={idx}
                className="px-4 py-2 rounded-full bg-[var(--primary)]/10 border border-[var(--primary)]/30 text-[var(--primary)] text-sm"
              >
                {keyword}
              </span>
            ))}
            <span className="px-4 py-2 rounded-full bg-white/10 text-gray-400 text-sm">
              site internet {zone.name.toLowerCase()}
            </span>
            <span className="px-4 py-2 rounded-full bg-white/10 text-gray-400 text-sm">
              webmaster {zone.name.toLowerCase()}
            </span>
            <span className="px-4 py-2 rounded-full bg-white/10 text-gray-400 text-sm">
              création site {zone.name.toLowerCase()}
            </span>
          </div>
        </div>
      </section>

      {/* Autres zones */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-white font-['Space_Grotesk']">
              Autres zones d'intervention
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {otherZones.map((otherZone, idx) => (
              <motion.div
                key={otherZone.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
              >
                <Link
                  to={`/zone-intervention/${otherZone.slug}`}
                  className="glass-panel rounded-xl p-4 block text-center hover:border-[var(--primary)]/50 transition-colors group"
                >
                  <FaMapMarkerAlt className="text-[var(--primary)] mx-auto mb-2" />
                  <span className="text-white font-medium group-hover:text-[var(--primary)] transition-colors">
                    {otherZone.name}
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-panel rounded-3xl p-12 text-center border-[var(--primary)]/30"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-['Space_Grotesk']">
              Besoin d'un expert web à {zone.name} ?
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Contactez-nous pour discuter de votre projet. Premier rendez-vous gratuit.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/contact"
                className="btn-magnetic px-8 py-4 bg-[var(--primary)] text-white font-bold rounded-xl"
              >
                Demander un devis gratuit
              </Link>
              <a
                href="tel:+33689177385"
                className="px-8 py-4 border border-white/20 text-white font-bold rounded-xl hover:bg-white/5 transition-all"
              >
                06 89 17 73 85
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ZoneIntervention;
